import React, { useState, useEffect, useCallback } from 'react';
import { Input, Checkbox, DatePicker } from 'antd';
import { LeftOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { handleCurrentFilter } from '@/utils/filters/currentFilter';
import { handleActionCurrentFilter } from '@/utils/filters/ActionItemCurrentFilter';
import { handleImpactCurrentFilter } from '@/utils/filters/ImpactCurrentFilter';
import { handleKpiCurrentFilter } from '@/utils/filters/KpiCurrentFilter';
import { handleMilestoneCurrentFilter } from '@/utils/filters/MilestoneCurrentFilter';
import { useUserData, useWorkstreamData } from './DataHooks';
import { extractCombinedNames } from '@/utils/AttributeFilterUtils';
import determineStage from '@/utils/DetermineStage';
import PeriodDateComponent from '@/utils/filters/PeriodDateComponent';
import { handlePeriodCurrentFilter } from '@/utils/filters/periodCurrentFilter';

const { RangePicker } = DatePicker;

const AttributeFilters = ({
  type,
  initiatives,
  actionItem,
  currentFilter,
  setCurrentFilter,
  impactCurrentFilter,
  setImpactCurrentFilter,
  kpiCurrentFilter,
  setKpiCurrentFilter,
  milestoneCurrentFilter,
  periodCurrentFilter,
  setPeriodCurrentFilter,
  setMilestoneCurrentFilter,
  actionCurrentFilter,
  setActionCurrentFilter,
  setInitiativeFilterType,
  attributeFilters = {},
  setAttributeFilters,
  searchTerm,
  setSearchTerm,
  subsectors
}) => {
  const users = useUserData(); // Assuming you have users data from useUserData hook
  const workstreams = useWorkstreamData();
  const [filterHistory, setFilterHistory] = useState([]);
  const [searchQuery, setSearchQuery] = useState(''); // State for search query
  const initiativeOwnerNames = extractCombinedNames(initiatives, 'initiativeOwnerInfo');
  const initiativeBackupNames = extractCombinedNames(initiatives, 'initiativeOwnerBackupInfo');
  const workstreamSponsorNames = extractCombinedNames(initiatives, 'workstreamSponsorInfo');
  const workstreamLeadNames = extractCombinedNames(initiatives, 'workstreamLeadInfo');
  const AllowOnlyTo = extractCombinedNames(initiatives, 'AllowOnlyTo');
  const crossFunctionalSupportWorkstream = [
    ...new Set(
      initiatives.flatMap(
        (initiative) =>
          initiative.crossFunctionalSupportWorkstreamInfo?.map((w) => w.subworkstreamName) || []
      )
    ),
  ];
  const Subworkstream = [
    ...new Set(
      initiatives.flatMap(
        (initiative) =>
          initiative.workstreamLeadSubworkstream?.map((s) => s.subworkstreamName) || []
      )
    ),
  ];
  const workstreamSponsorWorkstreamNames = [
    ...new Set(
      initiatives.flatMap(
        (initiative) => initiative.workstreamSponsorWorkstream?.map((w) => w.workstreamName) || []
      )
    ),
  ];
  const strategyInfo = [
    ...new Set(
      initiatives.flatMap(
        (initiative) =>
          initiative.strategyInfo?.map((s) => s.strategyName)
      )
    )
  ]
  const handleDateRangeChange = (dates, dateStrings) => {
    setAttributeFilters({
      ...attributeFilters,
      createdDateRange: dates,
    });
  };
  const staticImpactMenus = [
    { label: '#', key: 'impactNumber' },
    { label: 'Frequency & direction', key: 'frequencyAndDirection' },
    { label: 'What is the type of impact?', key: 'typeOfImpact' },
    { label: 'Sector', key: 'sector' },
    { label: 'Subsector', key: 'subsector' },
    { label: 'Whose P&L does this impact?', key: 'Whose P&L does this impact?' },
    {
      label: 'Actual impact update check (for last completed and previous period)',
      key: 'Actual impact update check (for last completed and previous period)',
    },
    {
      label: 'Planned value last update date (calendarized impact)',
      key: 'Planned value last update date (calendarized impact)',
    },
    {
      label: 'Actual value last update date (calendarized impact)',
      key: 'Actual value last update date (calendarized impact)',
    },
  ];
  const staticMilestoneMenus = [
    { label: '#', key: 'milestoneNumber' },
    { label: 'Name', key: 'name' },
    { label: '# - Name', key: 'milestoneNumber - name' },
    { label: 'Milestone owner', key: 'Owner' },
    { label: 'Status', key: 'Milestone Status' },
    { label: 'Type', key: 'type' },
    { label: 'Work starting date', key: 'Work starting date' },
    { label: 'Milestone completion date', key: 'Milestone completion date' },
    {
      label: 'Change in Milestone completion date (binned, current vs last week)',
      key: 'Change in Milestone completion date (binned, current vs last week)',
    },
    { label: 'Planning summary (simplified)', key: 'Planning summary (simplified)' },
    { label: 'Planning summary', key: 'Milestone Planning summary' },
    { label: '(LW) Planning summary', key: '(LW) Planning summary' },
    { label: 'Planning check', key: 'planningCheck' },
    { label: 'Created on', key: 'Created On' },
    { label: 'Created by', key: 'Created by' },
    { label: 'Updated on', key: 'Updated On' },
    { label: 'Updated by', key: 'Updated by' },
  ];
  // Define staticMenus outside the switch statement
  const staticMenus = [
    { label: '#', key: 'initiativeNumber', group: 'General' },
    { label: '# - Name', key: 'initiativeNumber - initiativeName', group: 'General' },
    { label: ' Name', key: 'initiativeName', group: 'General' },
    { label: ' Stage', key: 'stageSimplified', group: 'General' },
    { label: ' Stage (Simplified)', key: 'stage', group: 'Stage' },
    { label: ' Weekly Status', key: 'weeklyStatus', group: 'Stage' },
    { label: ' Initiative Owner', key: 'initiativeOwnerInfo', group: 'Team' },
    { label: 'Initiative Owner (Back up)', key: 'initiativeOwnerBackupInfo', group: 'Team' },
    { label: 'Workstream Lead', key: 'workstreamLeadInfo', group: 'Team' },
    { label: 'Workstream Sponsor', key: 'workstreamSponsorInfo', group: 'Team' },
    { label: ' Accountable Workstream', key: 'workstreamSponsorWorkstream', group: 'General' },
    { label: 'Subworkstream', key: 'workstreamLeadSubworkstream', group: 'General' },
    { label: 'Sector', key: 'sectorInfo', group: 'General' },
    { label: 'Subsector', key: 'subsectorInfo', group: 'General' },
    { label: 'Type', key: 'Type', group: 'General' },
    { label: 'Segment', key: 'initiativeCategory', group: 'General' },
    { label: 'Size', key: 'size', group: 'General' },
    { label: 'Subsegment', key: 'segment', group: 'General' },
    {
      label: ' Cross Functional Workstream Support',
      key: 'crossFunctionalSupportWorkstreamInfo',
      group: 'General',
    },
    {
      label: 'Strategy Belongs To',
      key: 'strategyInfo',
      group: 'General',
    },
    { label: 'Description', key: 'Description', group: 'General' },
    { label: ' Access', key: 'AllowOnlyTo', group: 'Team' },
    {
      label: ' Selected For To Meeting Descusion',
      key: 'selectedForToMeetingDescusion',
      group: 'General',
    },
    {
      label: ' Current initiative owner effective date',
      key: 'Current initiative owner effective date',
      group: 'Date',
    },
    {
      label: ' Workstream lead pending approval',
      key: 'Workstream lead pending approval',
      group: 'Stage',
    },
    {
      label: ' Workstream sponsor pending approval',
      key: 'Workstream sponsor pending approval',
      group: 'Stage',
    },
    { label: ' TO finance pending approval', key: 'TO finance pending approval', group: 'Stage' },
    {
      label: ' Strategy analyst pending approval',
      key: 'Strategy analyst pending approval',
      group: 'Stage',
    },
    {
      label: ' Workstream lead pending approvers',
      key: 'Workstream lead pending approvers',
      group: 'Team',
    },
    {
      label: ' Workstream sponsor pending approvers',
      key: 'Workstream sponsor pending approvers',
      group: 'Team',
    },
    { label: ' TO finance pending approvers', key: 'TO finance pending approvers', group: 'Team' },
    {
      label: ' Strategy analyst pending approvers',
      key: 'Strategy analyst pending approvers',
      group: 'Team',
    },
    {
      label: ' Net recurring benefit range(Latest Estimate, annualized)',
      key: 'Net recurring benefit range',
      group: 'Impact summary',
    },
    { label: ' Planning summary', key: 'Planning summary' },
    {
      label: ' Planning summary (Simplified)',
      key: 'Planning summary simplified',
      group: 'Timing Information',
    },
    {
      label: ' Actual impact update check (for last completed and previous period)',
      key: 'Actual impact update check (for last completed and previous period)',
      group: 'Impact summary',
    },
    {
      label: ' (FY23-24) Actual impact update check',
      key: '(FY23-24) Actual impact update check',
      group: 'Impact summary',
    },
    {
      label: ' (FY24-25) Actual impact update check',
      key: '(FY24-25) Actual impact update check',
      group: 'Impact summary',
    },
    {
      label: '(FY25-26) Actual impact update check',
      key: '(FY25-26) Actual impact update check',
      group: 'Impact summary'
    },
    {
      label: 'L3 date compliance check (forecast vs planned date)',
      key: 'L3 date compliance check (forecast vs planned date)',
      group: 'Timing Information',
    },
    {
      label: 'L4 date compliance check (forecast vs planned date)',
      key: 'L4 date compliance check (forecast vs planned date)',
      group: 'Timing Information',
    },
    {
      label: 'Actual KPI update check (for last completed and previous period)',
      key: 'Actual KPI update check (for last completed and previous period)',
      group: 'KPI summary',
    },

    // Add the image's data to the staticMenus array
    { label: 'L1 planned date', key: 'L1 planned date', group: 'Dates' },
    { label: 'L1 Original Planned Date', key: 'L1 Original Planned Date', group: 'Dates' },
    { label: 'L2 planned date', key: 'L2 planned date', group: 'Dates' },
    { label: 'L2 Original Planned Date', key: 'L2 Original Planned Date', group: 'Dates' },
    { label: 'L3 planned date', key: 'L3 planned date', group: 'Dates' },
    { label: 'L3 Original Planned Date', key: 'L3 Original Planned Date', group: 'Dates' },
    { label: 'L4 planned date', key: 'L4 planned date', group: 'Dates' },
    { label: 'L4 Original Planned Date', key: 'L4 Original Planned Date', group: 'Dates' },
    { label: 'L5 planned date', key: 'L5 planned date', group: 'Dates' },
    { label: 'L5 Original Planned Date', key: 'L5 Original Planned Date', group: 'Dates' },
    { label: 'L0 actual date', key: 'L0 actual date', group: 'Dates' },
    { label: 'L1 actual date', key: 'L1 actual date', group: 'Dates' },
    { label: 'L2 actual date', key: 'L2 actual date', group: 'Dates' },
    { label: 'L3 actual date', key: 'L3 actual date', group: 'Dates' },
    { label: 'L4 actual date', key: 'L4 actual date', group: 'Dates' },
    { label: 'L5 actual date', key: 'L5 actual date', group: 'Dates' },
    { label: 'Sub . for L1 approv . date', key: 'Sub . for L1 approv . date', group: 'Dates' },
    { label: 'Sub . for L2 approv . date', key: 'Sub . for L2 approv . date', group: 'Dates' },
    { label: 'Sub . for L3 approv . date', key: 'Sub . for L3 approv . date', group: 'Dates' },
    { label: 'Sub . for L4 approv . date', key: 'Sub . for L4 approv . date', group: 'Dates' },
    { label: 'Sub . for L5 approv . date', key: 'Sub . for L5 approv . date', group: 'Dates' },
    { label: 'Cancellation date', key: 'Cancellation date', group: 'Dates' },
    {
      label: 'Pipeline weekly variance (L0+)',
      key: 'Pipeline weekly variance (L0+)',
    },
    {
      label: 'Pipeline weekly variance (L1+)',
      key: 'Pipeline weekly variance (L1+)',
      group: 'Change (current vs last week)',
    },
    {
      label: 'Pipeline weekly variance (L2+)',
      key: 'Pipeline weekly variance (L2+)',
      group: 'Change (current vs last week)',
    },
    {
      label: 'Pipeline weekly variance (L3+)',
      key: 'Pipeline weekly variance (L3+)',
      group: 'Change (current vs last week)',
    },
    {
      label: 'Pipeline weekly variance (L4+)',
      key: 'Pipeline weekly variance (L4+)',
      group: 'Change (current vs last week)',
    },
    {
      label: 'Number of attachments',
      key: 'Number of attachments',
      group: 'Other',
    },

    {
      label: 'Has financial impacts?',
      key: 'hasFinancialImpact',
      group: 'Other',
    },
    {
      label: 'Is Customer Related?',
      key: 'forSpecificCustomer',
      group: 'Other'
    },
    {
      label: 'Segmetal PBT?',
      key: 'segmentalPBT',
      group: 'Other'
    },
    {
      label: 'Has qualitative KPI?',
      key: 'Has qualitative KPI?',
      group: 'Other',
    },

    {
      label: 'Future implementation costs range',
      key: 'Future implementation costs range',
      group: 'Sequencing',
    },
  ];
  const staticKpiMenus = [
    { label: 'Type of KPI (binned)', key: 'TypeOfKPI' },
    { label: 'Universal KPI', key: 'UniversalKPI' },
    { label: 'Sector', key: 'kpiSector' },
    { label: 'Subsector', key: 'kpiSubsector' },
    { label: 'Aggregation mode', key: 'aggregationMode' },
    { label: 'Measure and unit', key: 'unitOfMeasurement' },
  ];
  const staticWorkstreamActionMenus = [
    { label: '#', key: 'actionItemNumber' },
    { label: 'Name', key: 'workstreamActionName' },
    { label: 'Accountable Workstream', key: 'Accountable Workstream' },
    { label: 'Accountable subworkstream', key: 'Accountable subworkstream' },
    { label: 'Workstream Action Owner', key: 'owner' },
    { label: 'Collaborator', key: 'collaborator' },
    { label: 'Status', key: 'status' },
    { label: 'Source', key: 'source' },
    { label: 'Planning summary', key: 'planningSummary' },
    { label: 'Created on', key: 'Created on' },
    { label: 'Created by', key: 'createdBy' },
    { label: 'Updated on', key: 'Updated on' },
    { label: 'Updated by', key: 'Updated By' },
  ];
  useEffect(() => {
    let defaultOptions = [];
    let shouldSet = false;

    if (milestoneCurrentFilter === 'Planning summary (simplified)') {
      defaultOptions = [
        '<not set>', 'Not planned', 'Late', 'Late to start', 'Lately Started', 'Lately Completed', 'Not started',
        'Due soon', 'Due soon for 30 days', 'On plan', 'Completed', 'Cancelled', 'Initiative cancelled',
        'Initiative on hold',
      ].sort();
      shouldSet = !attributeFilters[milestoneCurrentFilter];
    } else if (milestoneCurrentFilter === 'Milestone Status') {
      defaultOptions = [
        'Not Started', 'Started', 'Completed', 'Cancelled'
      ].sort();
      shouldSet = !attributeFilters[milestoneCurrentFilter];
    } else if (
      milestoneCurrentFilter === 'Milestone Planning summary' ||
      milestoneCurrentFilter === '(LW) Planning summary'
    ) {
      defaultOptions = [
        '<not set>', 'Not planned', 'Late (more than 4 weeks)', 'Late (1 to 4 weeks)',
        'Late (less than 1 week)', 'Late to start', 'Lately Completed (more than 4 weeks)', 'Lately Completed (1 to 4 weeks)',
        'Lately Completed (less than 1 week)', 'Late to start (1 to 4 weeks)', 'Late to start (less than 1 week)', 'Late to start (more than 4 weeks)',
        'Lately Started (less than 1 week)', 'Lately Started (1 to 4 weeks)', 'Lately Started (more than 4 weeks)', 'Not started', 'Due soon', 'Due soon for 30 days',
        'On plan', 'Completed', 'Cancelled', 'Initiative on hold', 'Initiative cancelled',
      ].sort();
      shouldSet = !attributeFilters[milestoneCurrentFilter];
    }

    if (shouldSet) {
      setAttributeFilters(prevFilters => ({
        ...prevFilters,
        [milestoneCurrentFilter]: defaultOptions,
      }));
    }
  }, [milestoneCurrentFilter, attributeFilters, setAttributeFilters]); // Dependencies for useEffect

  switch (type) {
    case 'initiative':
      const filteredAttributes = staticMenus.filter(
        (attr) =>
          attr.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (attr.group && attr.group.toLowerCase().includes(searchQuery.toLowerCase()))
      );

      const groupedAttributes = {};
      filteredAttributes.forEach((attr) => {
        if (attr.group) {
          if (!groupedAttributes[attr.group]) {
            groupedAttributes[attr.group] = [];
          }
          groupedAttributes[attr.group].push(attr);
        } else {
          if (!groupedAttributes['']) {
            groupedAttributes[''] = [];
          }
          groupedAttributes[''].push(attr); // Add ungrouped
        }
      });

      return (
        <div style={{ padding: '10px' }}>
          <div style={{ marginBottom: '10px' }}>
            <Input
              placeholder="Search filters..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              prefix={<SearchOutlined />}
              style={{ width: '100%' }}
            />
          </div>
          {Object.entries(groupedAttributes).map(([groupName, attributes]) => (
            <div key={groupName}>
              {groupName && (
                <div style={{ fontWeight: 'bold', marginTop: '10px', fontSize: 14, color: '#333' }}>
                  {groupName}
                </div>
              )}
              {attributes.map((attr) => (
                <div
                  key={attr.key}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px',
                    borderBottom: '1px solid #ddd',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    setFilterHistory([...filterHistory, currentFilter]);
                    setCurrentFilter(attr.key);
                    setInitiativeFilterType(null);
                    setSearchQuery(''); // Clear search query on selection
                  }}
                >
                  <span>{attr.label}</span>
                  <RightOutlined />
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    case 'impact':
      return (
        <div style={{ padding: '10px' }}>
          {staticImpactMenus.map((menu) => (
            <div
              key={menu.key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px',
                borderBottom: '1px solid #ddd',
                cursor: 'pointer',
              }}
              onClick={() => {
                setFilterHistory([...filterHistory, impactCurrentFilter]);
                setImpactCurrentFilter(menu.key);
                setInitiativeFilterType(null);
              }}
            >
              <span>{menu.label}</span>
              <RightOutlined />
            </div>
          ))}
        </div>
      );

    case 'kpi':
      return (
        <div style={{ padding: '10px' }}>
          {staticKpiMenus.map((menu) => (
            <div
              key={menu.key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px',
                borderBottom: '1px solid #ddd',
                cursor: 'pointer',
              }}
              onClick={() => {
                setFilterHistory([...filterHistory, kpiCurrentFilter]);
                setKpiCurrentFilter(menu.key);
                setInitiativeFilterType(null);
              }}
            >
              <span>{menu.label}</span>
              <RightOutlined />
            </div>
          ))}
        </div>
      );

    case 'milestone':
      return (
        <div style={{ padding: '10px' }}>
          {staticMilestoneMenus.map((menu) => (
            <div
              key={menu.key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px',
                borderBottom: '1px solid #ddd',
                cursor: 'pointer',
              }}
              onClick={() => {
                setFilterHistory([...filterHistory, milestoneCurrentFilter]);
                setMilestoneCurrentFilter(menu.key);
                setInitiativeFilterType(null);
              }}
            >
              <span>{menu.label}</span>
              <RightOutlined />
            </div>
          ))}
        </div>
      );
    case 'workstreamAction':
      return (
        <div style={{ padding: '10px' }}>
          {staticWorkstreamActionMenus.map((menu) => (
            <div
              key={menu.key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px',
                borderBottom: '1px solid #ddd',
                cursor: 'pointer',
              }}
              onClick={() => {
                setFilterHistory([...filterHistory, actionCurrentFilter]);
                setActionCurrentFilter(menu.key);
                setInitiativeFilterType(null);
              }}
            >
              <span>{menu.label}</span>
              <RightOutlined />
            </div>
          ))}
        </div>
      );
    case 'period':
      return (
        <div style={{ padding: '10px' }}>
          {/* This block was the problem, it should directly render the next level */}
          <PeriodDateComponent
            periodCurrentFilter={periodCurrentFilter}
            attributeFilters={attributeFilters}
            setAttributeFilters={setAttributeFilters}
          />
        </div>
      );
    case currentFilter:
      return handleCurrentFilter(
        currentFilter,
        initiatives,
        attributeFilters,
        setAttributeFilters,
        searchTerm,
        setSearchTerm,
        determineStage,
        initiativeOwnerNames,
        initiativeBackupNames,
        workstreamSponsorNames,
        workstreamLeadNames,
        workstreamSponsorWorkstreamNames,
        AllowOnlyTo,
        crossFunctionalSupportWorkstream,
        strategyInfo,
        Subworkstream,
        users,
        subsectors
      );
    case impactCurrentFilter:
      return handleImpactCurrentFilter(
        impactCurrentFilter,
        initiatives,
        attributeFilters,
        setAttributeFilters,
        searchTerm,
        setSearchTerm,
        workstreams,
        subsectors
      );

    case kpiCurrentFilter:
      return handleKpiCurrentFilter(
        kpiCurrentFilter,
        initiatives,
        attributeFilters,
        setAttributeFilters,
        searchTerm,
        setSearchTerm,
        subsectors
      );

    case milestoneCurrentFilter:
      return handleMilestoneCurrentFilter(
        milestoneCurrentFilter,
        initiatives,
        attributeFilters,
        setAttributeFilters,
        searchTerm,
        setSearchTerm,
        users
      );

    case actionCurrentFilter:
      return handleActionCurrentFilter(
        actionCurrentFilter,
        actionItem,
        attributeFilters,
        setAttributeFilters,
        searchTerm,
        setSearchTerm,
        users,
        handleDateRangeChange
      );
    case periodCurrentFilter:
      return handlePeriodCurrentFilter(
        periodCurrentFilter,
        attributeFilters,
        setAttributeFilters
      );
    default:
      return null;
  }
};

export default AttributeFilters;
